import { ExceptionErrorBoundary } from "@/components/ExceptionHandler";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import type { TrackLocation } from "@/hooks/use-segment";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import * as React from "react";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;

const DialogClose = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Close>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, onClick, ...props }, ref) => (
    <DialogPrimitive.Close
        {...props}
        ref={ref}
        className={className}
        onClick={(e) => {
            e.stopPropagation();
            if (onClick) {
                onClick(e);
            }
        }}
    />
));
DialogClose.displayName = DialogPrimitive.Close.displayName;

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            "fixed inset-0 z-50 bg-surface-primary/50 backdrop-blur-modal backdrop-opacity-modal data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            className,
        )}
        {...props}
    />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogTriggerProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    disableOverlay?: boolean;
    "data-track-location": TrackLocation;
};
const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogTriggerProps>(
    ({ className, children, disableOverlay, "data-track-location": dataTrackLocation, ...props }, ref) => {
        const content = (
            <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                <DialogPrimitive.Content
                    ref={ref}
                    className={cn(
                        // layout
                        "relative flex max-h-[90%] w-full max-w-lg flex-col overflow-x-auto p-6",
                        // appearance
                        "rounded-xl border border-border-base bg-surface-popover shadow-modal",
                        // animations
                        "duration-200 @container data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                        className,
                    )}
                    {...props}
                    data-track-location={dataTrackLocation}
                >
                    {children}
                    <DialogClose asChild>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="absolute right-4 top-4 p-2 text-content-muted"
                            aria-label="Close"
                        >
                            <IconCrossSmall />
                        </Button>
                    </DialogClose>
                </DialogPrimitive.Content>
            </div>
        );

        return (
            <ExceptionErrorBoundary>
                <DialogPortal>{!disableOverlay ? <DialogOverlay>{content}</DialogOverlay> : content}</DialogPortal>
            </ExceptionErrorBoundary>
        );
    },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <>
        <header className={cn("mb-6 flex flex-col gap-1.5 text-left", className)} {...props} />
    </>
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <footer
        className={cn(
            "flex flex-col-reverse justify-end gap-2 sm:flex-row",
            "mt-6 flex-row justify-between",
            className,
        )}
        {...props}
    />
);

DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn("text-lg font-semibold leading-none tracking-tight text-content-primary", className)}
        {...props}
    />
));

DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn("m-0 p-0 text-base text-content-secondary", className)}
        {...props}
    />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            "overflow-x-visible px-1 py-8 text-base text-content-primary",
            "py-1.5 text-content-muted",
            className,
        )}
        {...props}
    />
);
DialogBody.displayName = "DialogBody";

export {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
};
